import React, { useEffect, useState } from "react";
import { API_URL, getAssetImage } from "../../../constant";
import { useAuth } from "../../../context/auth-context";
import axiosInstance from "../../../utils/axiosConfig";
import { Welcome } from "../../../types";
import { formatAmount } from "../../../utils/FormatPerson";
import { TailSpin } from "react-loader-spinner";
import { toast } from "react-hot-toast";
import { resolveBankNameFromLists } from "../../../lib/api/bankNameResolver";

interface TransactionDetailsModalProps {
  onClose: () => void;
  transaction: any;
  setRefetch: any;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  onClose,
  transaction,
  setRefetch,
}) => {
  const [customerLoader, setCustomerLoader] = useState<boolean>(false);
  const [approvalLoader, setApprovalLoader] = useState<boolean>(false);
  const [declineLoader, setDeclineLoader] = useState<boolean>(false);
  const [data, setData] = useState<Welcome>();
  const [allBanks, setAllBanks] = useState<any[]>([]);
  const [allOldBanks, setAllOldBanks] = useState<any[]>([]);

  const { isAuthenticated } = useAuth();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";

    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");

    return `${month} ${day}${getDaySuffix(
      day
    )}, ${year} | ${formattedHours}:${formattedMinutes}${ampm}`;
  };
  const getDaySuffix = (day: number) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return; // Don't fetch before the session bootstrap has confirmed one

    setCustomerLoader(true);

    const fetchData = async () => {
      try {
        const { data } = await axiosInstance.get(
          `${API_URL}api/rules/transactions/${transaction._id}`
        );

        console.log(data.data);
        setData(data.data);
      } catch (error: any) {
        console.log(error.response?.data?.message || "An error occurred");
      } finally {
        setCustomerLoader(false);
      }
    };
    fetchData();
  }, [isAuthenticated, transaction._id]);

  useEffect(() => {
    Promise.all([
      axiosInstance.get(`${API_URL}api/banks/palmpay`),
      axiosInstance.get(`${API_URL}api/banks`),
    ])
      .then(([palmpayRes, oldRes]: any[]) => {
        setAllBanks(
          Array.isArray(palmpayRes?.data?.data) ? palmpayRes.data.data : []
        );
        setAllOldBanks(
          Array.isArray(oldRes?.data?.data) ? oldRes.data.data : []
        );
      })
      .catch((e: any) => {
        console.log(e?.response?.data?.message);
      });
  }, []);

  const approve = async () => {
    setApprovalLoader(true);
    try {
      const { data: data1 } = await axiosInstance.put(
        `${API_URL}api/rules/transactions/release`,
        {
          transactionId: data?.transaction?._id,
          adminAction: "APPROVE", // APPROVE or DECLINE
        }
      );
      toast.success(`Transaction approved successfully`);
      setRefetch((prev: number) => prev++);
      onClose();
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setApprovalLoader(false);
    }
  };
  const decline = async () => {
    setDeclineLoader(true);
    try {
      const { data: data2 } = await axiosInstance.put(
        `${API_URL}api/rules/transactions/release`,
        {
          transactionId: data?.transaction?._id,
          adminAction: "DECLINE", // APPROVE or DECLINE
        }
      );
      toast.success(`Transaction declined successfully`);
      setRefetch((prev: number) => prev++);
      onClose();
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setDeclineLoader(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center font-manrope justify-center bg-black bg-opacity-80">
      <div className="w-full max-w-lg ">
        {/* Header */}
        <div className="bg-[#0D0D0D] rounded-2xl p-8 w-full max-w-lg shadow-lg relative">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-white text-xl font-bold">
              Transactions details
            </h2>
            <button
              onClick={onClose}
              className="text-[#78C2F7] text-base font-semibold"
            >
              Close
            </button>
          </div>
          {/* Crypto Info */}
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-full  flex items-center justify-center">
              {getAssetImage(data?.triggerSummary?.asset) ? (
                <img
                  src={getAssetImage(data?.triggerSummary?.asset)}
                  alt="coin"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-[11px] font-bold text-[#869AA9]">
                  {String(data?.triggerSummary?.asset ?? "")
                    .toUpperCase()
                    .slice(0, 3)}
                </span>
              )}
            </div>
            <div>
              <div className="text-[#DEE1E6] font-sora text-[24px] font-bold">
                {formatAmount(data?.triggerSummary?.amount)}{" "}
                {data?.triggerSummary?.asset}
              </div>
              <div className="text-[#0B75C2] text-[13px] font-medium">
                ≈ {formatAmount(data?.triggerSummary?.usdtAmount)}
              </div>
            </div>
          </div>
          {/* Approve/Decline Buttons */}
          <div className="flex w-4/5 gap-4 my-6">
            {data?.transaction?.status.toLowerCase() === "pending" ||
            data?.transaction?.status.toLowerCase() === "approved" ? (
              <button
                onClick={decline}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border border-[#3A3A3A] bg-[#23282D] text-[#BDBDBE] text-lg font-semibold hover:bg-[#23282D]/80"
              >
                <span className="text-2xl">
                  {" "}
                  {declineLoader ? (
                    <TailSpin color="#ffffff" width={20} height={20} />
                  ) : (
                    "🚫"
                  )}
                </span>{" "}
                Decline
              </button>
            ) : null}
            {data?.transaction?.status.toLowerCase() === "pending" ||
            data?.transaction?.status.toLowerCase() === "rejected" ? (
              <button
                onClick={approve}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-[#1ED760] text-[#181B1F] text-lg font-semibold hover:bg-[#1ED760]/90"
              >
                <span className="text-2xl">
                  {approvalLoader ? (
                    <TailSpin color="#ffffff" width={20} height={20} />
                  ) : (
                    "✔️"
                  )}
                </span>{" "}
                Approve
              </button>
            ) : null}
          </div>
        </div>
        {/* Details Section */}
        <div className="bg-[#0D0D0D] rounded-2xl p-6 mt-4">
          <div className="grid grid-cols-2 gap-y-3 gap-x-4">
            <div className="text-[#AEBCC6] font-semibold text-sm">
              Received from
            </div>
            <div className="text-secondary_button font-bold text-sm  text-right">
              -----
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">Network</div>
            <div className="text-[#AEBCC6] text-sm font-bold text-right">
              ----
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">Rate</div>
            <div className="text-[#AEBCC6] font-bold text-sm text-right">
              ----
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">Status</div>
            <div
              className={` ${
                data?.transaction.status.toLowerCase() === "approved"
                  ? " text-[#1ED760]"
                  : data?.transaction.status.toLowerCase() === "pending"
                  ? " text-[#CB9200]"
                  : data?.transaction.status.toLowerCase() === "rejected"
                  ? " text-red-500"
                  : " text-white"
              } font-bold text-sm text-right`}
            >
              {data?.transaction.status}
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">
              Cash Destination
            </div>
            <div className="text-secondary_button font-bold text-sm text-right">
              {data?.user?.banks.find((bank) => bank.isDefault)?.accountNumber}
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">
              Bank Name
            </div>
            <div className="text-white text-sm text-right">
              {resolveBankNameFromLists(
                data?.user?.banks.find((bank) => bank.isDefault)?.bankCode,
                allBanks,
                allOldBanks
              )}
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">
              Account Name
            </div>
            <div className="text-[#AEBCC6] font-bold text-sm text-right">
              {data?.user?.banks.find((bank) => bank.isDefault)?.accountName}
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">
              Time Stamp
            </div>
            <div className="text-[#AEBCC6] font-bold text-sm text-right">
              {formatDate(data?.triggerSummary?.timestamp as string)}
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">
              Amount to Pay
            </div>
            <div className="text-secondary_button font-bold text-sm text-right">
              ----
            </div>
            <div className="text-[#AEBCC6] font-semibold text-sm">Order ID</div>
            <div className="text-secondary_button font-bold text-sm text-right">
              {data?.transaction.transactionId}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
